import { clsx } from "clsx";
import { useEffect, useState } from "react";
import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Section } from "@/components/Section";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import type { BotConnectInterestOption, LeadMagnetNextStepsPropsSchema } from "@/schemas/sections";
import type { PostSubmitContext } from "@/types/config";
import type { SectionWrapperProps } from "@/types/section-wrapper";
import { trackLeadMagnetNext } from "@/utils/tracking";
import {
  contextFromPostSubmit,
  isWebinarReferrer,
  type PageContext,
  readPageContext,
  renderSubtitle,
  resolveContent,
} from "./content-resolution";
import { NextStepCardLink } from "./NextStepCardLink";
import { OnboardingStep } from "./OnboardingStep";
import {
  BotConnectInterestConfirmation,
  isBotConnectInterestEnabled,
  TelegramConnectBlock,
  useConnectGate,
} from "./TelegramConnectGate";
import { TONE_PALETTE_VARS } from "./tone-tokens";

export type LeadMagnetNextStepsSectionProps = z.input<typeof LeadMagnetNextStepsPropsSchema> &
  SectionWrapperProps & {
    // SSR-resolved post-submit context (email/source/kind) threaded from
    // +onBeforeRender via pageProps → renderSection, so the correct copy variant
    // is in the initial HTML (no post-hydration flash).
    postSubmit?: PostSubmitContext | null;
  };

function usePostSubmitContext(
  postSubmit: PostSubmitContext | null | undefined,
  mode: string,
): PageContext {
  const [ctx, setCtx] = useState<PageContext>(() => contextFromPostSubmit(postSubmit));
  useEffect(() => {
    const server = contextFromPostSubmit(postSubmit);
    const hasServer = server.email || server.source || server.kind || server.connectTokenAvailable;
    let next = hasServer ? server : readPageContext();
    if (!next.kind && isWebinarReferrer()) next = { ...next, kind: "webinar" };
    setCtx(next);
    trackLeadMagnetNext(next.source, mode);
  }, [mode, postSubmit]);
  return ctx;
}

export function LeadMagnetNextSteps({
  mode = "delivered",
  title,
  subtitle,
  subtitleNoEmail,
  spamTitle = "Письмо не пришло за пару минут?",
  spamNote,
  steps = [],
  supportEmail = "info@lenacareer.ru",
  nextTitle = "Что дальше?",
  nextSubtitle,
  telegramConnect,
  maxConnect,
  botConnectInterest,
  onboarding,
  cards = [],
  byKind,
  bySource,
  postSubmit,
  id,
  background = "base-100",
  padding = "lg",
  className,
}: LeadMagnetNextStepsSectionProps) {
  const ctx = usePostSubmitContext(postSubmit, mode);
  const {
    title: rTitle,
    subtitle: rSubtitle,
    subtitleNoEmail: rSubtitleNoEmail,
    spamTitle: rSpamTitle,
    spamNote: rSpamNote,
    steps: rSteps,
    nextTitle: resolvedNextTitle,
    nextSubtitle: resolvedNextSubtitle,
    telegramConnect: resolvedTelegramConnect,
    maxConnect: resolvedMaxConnect,
    botConnectInterest: resolvedBotConnectInterest,
    onboarding: resolvedOnboarding,
    cards: resolvedCards,
  } = resolveContent(
    {
      title,
      subtitle,
      subtitleNoEmail,
      spamTitle,
      spamNote,
      steps,
      nextTitle,
      nextSubtitle,
      telegramConnect,
      maxConnect,
      botConnectInterest,
      onboarding,
      cards,
    },
    ctx.kind ? byKind?.[ctx.kind] : undefined,
    ctx.source ? bySource?.[ctx.source] : undefined,
  );

  // An already-connected visitor (`botConnected`, resolved SSR-side from the `?t=`
  // token) never sees the connect module — no re-link CTA. With the block off,
  // `useConnectGate(undefined)` is inactive → `revealed` is true → the rest of
  // the page (onboarding + upsell cards) shows straight away.
  const connectEnabled =
    !!resolvedTelegramConnect && resolvedTelegramConnect.enabled !== false && !ctx.botConnected;
  const botConnectInterestEnabled = isBotConnectInterestEnabled(
    connectEnabled,
    ctx.connectTokenAvailable,
    resolvedBotConnectInterest,
  );
  const gate = useConnectGate(connectEnabled ? resolvedTelegramConnect : undefined);
  const [selectedBotConnectInterest, setSelectedBotConnectInterest] =
    useState<BotConnectInterestOption | null>(null);
  const showOnboarding =
    gate.revealed && !!resolvedOnboarding && resolvedOnboarding.enabled !== false;

  const selectBotConnectInterest = (option: BotConnectInterestOption) => {
    setSelectedBotConnectInterest(option);
    gate.skip();
  };

  return (
    <Section
      id={id}
      background={background}
      padding={padding}
      // While the gate is closed the section fills the viewport, so the footer
      // sits below the fold and the connect choices stay the primary action.
      // `svh` (not `vh`) — mobile browser chrome makes `vh` overshoot and push
      // the CTA itself out of view on exactly our 75%-mobile traffic.
      // `grid`+`content-center` rather than flex: ADR-0094 routes flex layout
      // through the Flex primitive, and Section's `containerLayout` only reaches
      // the inner container — the min-height has to live on the section itself.
      className={clsx(!gate.revealed && "grid min-h-svh content-center", className)}
      data-lm-source={ctx.source || undefined}
    >
      <div
        data-component="LeadMagnetNextSteps"
        className="mx-auto w-full max-w-3xl"
        style={TONE_PALETTE_VARS}
      >
        <div className="text-center">
          <div
            data-component="LeadMagnetSuccessIcon"
            className={clsx(botConnectInterestEnabled && "hidden md:block")}
          >
            <Flex
              aria-hidden="true"
              align="center"
              justify="center"
              className="mx-auto mb-3 h-12 w-12 rounded-full bg-brand/10 text-(length:--type-heading) md:mb-4 md:h-14 md:w-14 md:text-3xl"
            >
              {mode === "confirm" ? "✉️" : "🎉"}
            </Flex>
          </div>
          <Text variant="subheading" as="h1" className="text-fg">
            {rTitle}
          </Text>
          <Text variant="body-sm" as="p" className="mx-auto mt-2 max-w-xl text-muted">
            {renderSubtitle(rSubtitle, ctx.email, rSubtitleNoEmail)}
          </Text>
        </div>

        {connectEnabled && resolvedTelegramConnect && !selectedBotConnectInterest && (
          <TelegramConnectBlock
            cfg={resolvedTelegramConnect}
            gate={gate}
            maxConnect={resolvedMaxConnect}
            onMaxTapped={gate.markMaxTapped}
            botConnectInterest={botConnectInterestEnabled ? resolvedBotConnectInterest : undefined}
            onInterestSelected={selectBotConnectInterest}
            compact={botConnectInterestEnabled}
          />
        )}

        {selectedBotConnectInterest && (
          <BotConnectInterestConfirmation option={selectedBotConnectInterest} />
        )}

        {showOnboarding && resolvedOnboarding && <OnboardingStep cfg={resolvedOnboarding} />}

        {gate.revealed && resolvedCards.length > 0 && (
          <div className="mt-6 md:mt-8">
            <div className="mb-4 text-center">
              <Text variant="subheading" as="h2" className="text-fg">
                {resolvedNextTitle}
              </Text>
              {resolvedNextSubtitle && (
                <Text variant="body-sm" as="p" className="mx-auto mt-1 max-w-lg text-muted">
                  {resolvedNextSubtitle}
                </Text>
              )}
            </div>
            <div
              className={clsx(
                "grid gap-3",
                resolvedCards.length >= 3 ? "md:grid-cols-3" : "md:grid-cols-2",
              )}
            >
              {resolvedCards.map((card, index) => (
                <NextStepCardLink key={`${card.kind}:${card.href}`} card={card} index={index} />
              ))}
            </div>
          </div>
        )}

        <div className="mx-auto mt-6 max-w-xl rounded-2xl border border-border bg-bg p-4 text-left md:mt-8 md:p-6">
          <Text variant="label" as="p" className="flex items-center gap-2 text-fg">
            <span aria-hidden="true">📩</span>
            {rSpamTitle}
          </Text>
          {rSteps.length > 0 && (
            <Stack as="ol" gap={8} className="mt-3">
              {rSteps.map((step, index) => (
                <Flex as="li" key={step} align="start" gap={8}>
                  <Flex
                    as="span"
                    align="center"
                    justify="center"
                    className="mt-px h-5 w-5 shrink-0 rounded-full bg-brand text-(length:--type-caption) font-(weight:--fw-bold) text-brand-content"
                  >
                    {index + 1}
                  </Flex>
                  <Text variant="body-sm" as="span" className="flex-1 text-muted">
                    {step}
                  </Text>
                </Flex>
              ))}
            </Stack>
          )}
          <Text variant="body-sm" as="p" className="mt-3 text-muted">
            {rSpamNote} Не нашли письмо?{" "}
            <a
              href={`mailto:${supportEmail}`}
              data-track="lm_next.link.support"
              className="ds-link ds-link-primary font-(weight:--fw-medium)"
            >
              {supportEmail}
            </a>
          </Text>
        </div>
      </div>
    </Section>
  );
}

LeadMagnetNextSteps.displayName = "LeadMagnetNextSteps";
